<template>
    <v-main class="custom--v-main">
        <app-header>
            <h3 class="tw-text-[25px] tw-text-[#0D3659] tw-font-semibold">
                Reviews
            </h3>
            <template v-if="!isCustomer" #right>
                <v-btn depressed color="primary" class="v-btn v-size--medium" @click.prevent="openInviteReviewer">
                    <span>Invite a Reviewer</span>
                </v-btn>
            </template>
        </app-header>

        <app-customer-start-new-order-slider v-if="isCustomer"/>

        <div class="tw-m-[20px_30px]">
          <div class="tw-grid sm:tw-grid-cols-2 tw-gap-[20px] tw-mb-[20px]">
            <button
              class="tw-p-[10px_20px] tw-rounded-[4px] tw-font-medium"
              :class="getReviewsType === 0 ? 'tw-bg-secondary tw-text-white' : 'tw-bg-light tw-text-[gray]'"
              @click="loadMovingReviews"
            >
              Moving Reviews
            </button>
            <button
              class="tw-p-[10px_20px] tw-rounded-[4px] tw-font-medium"
              :class="getReviewsType === 1 ? 'tw-bg-secondary tw-text-white' : 'tw-bg-light tw-text-[gray]'"
              @click="loadDeliveryReviews"
            >
              Delivery Reviews
            </button>
          </div>

          <v-tabs
            v-model="tab"
            class="tw-mb-[10px]"
          >
            <v-tab v-for="item in tabs" :key="item.tab">
              {{ item.tab }}
            </v-tab>
          </v-tabs>

          <div class="tw-mb-[30px] tw-border-0 tw-border-b tw-border-solid tw-border-[#ddd] tw-font-medium tw-text-secondary">
            <p v-if="isCustomer">At TopHelpers.com, you can write reviews as a customer for your helpers. Your helpers can also write reviews for you in return. This two-way review system ensures that customers and helpers can provide honest feedback to each other.</p>
            <p v-else>On TopHelpers.com you can be reviewed and write a review for your clients as well. We do not edit or remove reviews of both sides. To keep the best standards, reviews will be archived after three years.</p>
          </div>

          <div v-if="isLoading" style="max-width: 1232px">
            <v-skeleton-loader
              v-bind="attrs"
              type="table-heading"
              elevation="0"
            >
            </v-skeleton-loader>
            <v-skeleton-loader
              v-for="n in 10"
              :key="n"
              v-bind="attrs"
              elevation="0"
              type="list-item-avatar-three-line, actions"
            >
            </v-skeleton-loader>
          </div>

          <div v-else>
            <div class="tw-grid tw-gap-[20px] tw-mb-[20px]">
              <div class="tw-flex tw-items-center tw-gap-[20px]">
                <v-rating
                  half-icon="fa-star-half-alt"
                  half-increments
                  readonly
                  color="#FBAB1E"
                  background-color="#FBAB1E"
                  class="custom--v-rating"
                  :value="getStars(tab)"
                />
                <span class="tw-font-medium tw-text-secondary">
                  {{ getReviewsCount }} out of 5
                </span>
              </div>
              <div class="tw-font-medium tw-text-secondary">
                {{ getReviewCount(tab) }}
              </div>
            </div>

            <v-tabs-items class="custom--v-tabs-items" v-model="tab">
              <v-tab-item>
                <review-card
                  v-for="(review, index) in reviews.sent.reviews"
                  :review="review"
                  :key="index"
                  :tab="tab"
                />
              </v-tab-item>
              <v-tab-item>
                <review-card
                  v-for="(review, index) in reviews.received.reviews"
                  :review="review"
                  :key="index"
                  :tab="tab"
                  received
                />
              </v-tab-item>
            </v-tabs-items>
          </div>
        </div>
    </v-main>
</template>

<script>
import ReviewCard from "@/views/Reviews/ReviewCard.vue";
import AppRating from "@/components/AppRating.vue";
import { mapActions } from "vuex/dist/vuex.esm.browser";
import { mapGetters } from "vuex";
import loading from "@/mixins/loading.js";
import InviteReviewer from "@/views/Dashboard/DashboardInviteReviewer.vue";
import AppCustomerStartNewOrderSlider from '@/components/AppCustomerStartNewOrderSlider.vue';
import UserVerifyEmail from '@/mixins/UserVerifyEmail';

export default {
    name: "Reviews",
    mixins: [ loading, UserVerifyEmail ],

    components: { AppCustomerStartNewOrderSlider, AppRating, ReviewCard },

    data () {
        return {
            attrs: {
                class: 'mb-5',
                boilerplate: false,
                elevation: 2,
            },
            reviewsType: 0, // 0 - moving, 1 - delivery
            tab: null,
            tabs: [
                { tab: 'Reviewed' },
                { tab: 'Received' }
            ],
        }
    },

    computed: {
        ...mapGetters('review', ['reviews']),
        ...mapGetters('auth', ['isCustomer']),

        getReviewsCount () {
            return this.tab === 0 ? Number(this.reviews.sent.avg_star).toFixed(1) : Number(this.reviews.received.avg_star).toFixed(1)
        },

        getReviewsType () {
            return this.reviewsType;
        }
    },

    methods: {
        ...mapActions('review', ['getMovingReviews', 'getDeliveryReviews']),

        loadMovingReviews () {
            this.startLoading(async () => {
                await this.getMovingReviews();
            });
            this.reviewsType = 0;
        },

        loadDeliveryReviews () {
          this.startLoading(async () => {
            await this.getDeliveryReviews();
          });
          this.reviewsType = 1;
        },

        getStars (tab) {
            return tab === 0 ? Number(this.reviews.sent.avg_star) : Number(this.reviews.received.avg_star)
        },

        getReviewCount (tab) {
            if (tab === 0) {
                return this.reviews.sent.count_star <= 1 ? this.reviews.sent.count_star + ' active review' : this.reviews.sent.count_star + ' active reviews';
            }
            return this.reviews.received.count_star <= 1 ? this.reviews.received.count_star + ' active review' : this.reviews.received.count_star + ' active reviews';
        },

        openInviteReviewer () {
            this.$modal.show(InviteReviewer, {}, {
                width: 814,
                height: 605,
            })
        },
    },

    created () {
        this.loadMovingReviews();
    },

    watch: {
        tab (v) {
            if (v === 0) {
                this.stars = {
                    avg_star: Number(this.reviews.sent.avg_star).toFixed(1),
                    count_star: this.reviews.sent.count_star
                }
            } else {
                this.stars = {
                    avg_star: Number(this.reviews.received.avg_star).toFixed(1),
                    count_star: this.reviews.received.count_star
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
::v-deep {
    .custom--v-container {
        @media (min-width: 968px) {
            margin-left: 18px !important;
            margin-right: 18px;
        }
    }
    .v-tabs > .v-tabs-bar, .v-tabs-items {
        background-color: #F5F5F9 !important;
    }

    .v-tab {
        width: 93px;
        height: 36px;
        border-radius: 4px;
        background: #fff;
        color: #0D3659;
        text-transform: capitalize;
        margin-right: 15px;
        font-size: 14px;
        font-weight: 500;
        line-height: 17px;
    }

    .v-tab--active {
        background: #8F3985 !important;
        color: #fff;
    }

    .v-tabs-slider {
        display: none;
    }

}
</style>
